import React, { memo } from "react";

import BoardRoleBadge from "@/features/player-kanban/components/molecules/BoardRoleBadge";

import { Box } from "@/components/atoms/Box";
import { Stack } from "@/components/atoms/Stack";
import { Pressable } from "@/components/atoms/Pressable";
import { Text } from "@/components/atoms/Text";
import { TEST_ID_PLAYER_KANBAN } from "e2e/testId";
import { UserKanbanBoardRole } from "@/generated/graphql";

/**
 * Props for BoardDropdownItemView component
 */
interface BoardDropdownItemViewProps {
  /** Display name of the board */
  readonly name: string;
  /** Whether this item is currently active/selected */
  readonly isActive: boolean;
  /** User's role on this board */
  readonly userRole?: UserKanbanBoardRole | null;
  /** Callback when item is pressed */
  readonly onPress: () => void;
}

/**
 * Individual board dropdown item presentation component
 * Displays a selectable board item with role badge and active indicator
 * @param root0 - Component props
 * @param root0.name - Display name of the board
 * @param root0.isActive - Whether this item is currently active/selected
 * @param root0.userRole - User's role on this board
 * @param root0.onPress - Callback when item is pressed
 */
const BoardDropdownItemView = ({
  name,
  isActive,
  userRole,
  onPress,
}: BoardDropdownItemViewProps) => (
  <Pressable
    onPress={onPress}
    testID={TEST_ID_PLAYER_KANBAN.BOARD_DROPDOWN_ITEM}
    className={`px-4 py-3 ${isActive ? "bg-accent-primary/10" : "bg-transparent"}`}
  >
    <Stack direction="horizontal" className="items-center justify-between">
      <Stack direction="horizontal" className="flex-1 items-center gap-2">
        <Text
          variant="body-strong"
          className={`${
            isActive ? "text-accent-primary" : "text-content-primary"
          }`}
        >
          {name}
        </Text>
        {userRole != null && <BoardRoleBadge role={userRole} />}
      </Stack>
      {isActive && <Box className="size-2 rounded-full bg-accent-primary" />}
    </Stack>
  </Pressable>
);

BoardDropdownItemView.displayName = "BoardDropdownItemView";

export default memo(BoardDropdownItemView);
